import {HttpClient} from '@angular/common/http';
import {AfterViewInit, Component, Inject, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Building} from '../building/building.model';
import {AppConfig} from '../config/app.config';
import 'rxjs/add/operator/map';
import {FileService} from '../file/file.service';
import {UIRouter} from '@uirouter/angular';
import {DashboardData} from './dashboard-data.interface';
import {LatLng, LatLngBounds, MapsAPILoader} from '@agm/core';
import {AuthenticationService} from '@ngx-ivengi/authentication';
import {ExportationModalService} from '../exportation/exportation-modal.service';

// This API object is not valid to use before ngAfterViewInit has been called.
// See https://github.com/SebastianM/angular2-google-maps/issues/782 for details.
declare var google: any;

@Component({
  selector: 'app-dashboard-client',
  templateUrl: './dashboard-client.component.html'
})
export class DashboardClientComponent implements OnInit {

  @Input() public data: DashboardData;
  public isLoaded: boolean = false;
  public warrantyChartData: {name: string, value: number}[];
  public chartColorScheme: any;
  public selectedMarker: {lat: number, lng: number, name: string};
  private statusToName = {};

  public map = {
    latlngBounds: null,
    markers: [],
    selectBoxMarkers: [],
  };

  constructor(
    @Inject(AppConfig) private appConfig,
    private http: HttpClient,
    private authService: AuthenticationService,
    private translate: TranslateService,
    private fileService: FileService,
    private router: UIRouter,
    private mapsApiLoader: MapsAPILoader,
    private exportationModalService: ExportationModalService,
  ) {}

  public ngOnInit(): void {

    this.data.buildings = this.data.buildings.map(building => {
      building.assignments = building.assignments.filter(assignment => assignment.status === 'PROCESSED');
      return building;
    });

    this.chartColorScheme = {
      domain: [
        '#60C060', '#33a3df', '#FF9900', '#DF4B33'
      ]
    };

    // add the markers to the map
    const markers = new Map<string, any>();

    this.data.buildings.forEach((buildingBlock: any) => {

      buildingBlock.building.legacyDrawingSrc = this.fileService.getLinkById(buildingBlock.building.legacyDrawingId);

      if(buildingBlock.building.coordinate && buildingBlock.building.coordinate.coordinates) {

        const key = `${buildingBlock.building.coordinate.coordinates[0]}|${buildingBlock.building.coordinate.coordinates[1]}`;

        const data = {
            name: buildingBlock.building.name,
            lat: buildingBlock.building.coordinate.coordinates[0],
            lng: buildingBlock.building.coordinate.coordinates[1],
        };

        this.map.selectBoxMarkers.push(JSON.parse(JSON.stringify(data)));

        if(markers.has(key)) {
          data.name = `${markers.get(key).name}<br /> ${data.name}`;
        }

        markers.set(key, data);

        this.map.markers.push(...Array.from(markers.values()));

        // this.map.markers.push(
        //   {
        //     name: buildingBlock.building.name,
        //     lat: buildingBlock.building.coordinate.coordinates[0],
        //     lng: buildingBlock.building.coordinate.coordinates[1],
        //   }
        // );
      }
    });

    // when the google maps API is loaded, set the bounding box
    this.mapsApiLoader.load().then(() => {
      this.createMapBounds();
    });

    this.data.buildings.map((item: any) => {
      item.isCollapsed = true;
      const chartData = [];
      item.assignmets = item.assignments.map(assignment => {
        assignment.isCollapsed = true;
        return assignment;
      });
      item.assignments.sort((a, b) => {
        const aRef = a.reference as string;
        const bRef = b.reference as string;
        return aRef.localeCompare(bRef);
      });
      for(const status in item.counts.warranty.channels) {
        this.statusToName[status.toUpperCase()] = this.translate.instant(`enumeration.warrantyStatus.${status.toUpperCase()}`);
        chartData.push({
          name: this.statusToName[status.toUpperCase()],
          value: item.counts.warranty.channels[status]
        });
      }
      item.counts.warranty.chartData = chartData;
      return item;
    });

    this.data.buildings.sort((a, b) => {
      return a.building.name.localeCompare(b.building.name);
    });

    const warrantyChartData: {name: string, value: number}[] = [];
    for(const status in this.data.overview.warranty.channels) {
      this.statusToName[status.toUpperCase()] = this.translate.instant(`enumeration.warrantyStatus.${status.toUpperCase()}`);
      warrantyChartData.push({
        name: this.statusToName[status.toUpperCase()],
        value: this.data.overview.warranty.channels[status],
      });
    }
    this.data.overview.warranty.chartData = warrantyChartData;

    this.isLoaded = true;
  }

  public getTooltipData(customerBlock: any): string {
    const tooltipText = [];

    for(const type in customerBlock.perType.throughput) {
      if(customerBlock.perType.throughput[type] > 0) {
        tooltipText.push(`${this.translate.instant('enumeration.throughputType.' + type)}: ${customerBlock.perType.throughput[type]}`);
      }
    }

    return tooltipText.join('<br />');
  }

  public getTooltipCount(customerBlock: any): number {
    for(const type in customerBlock.perType.throughput) {
      if(customerBlock.perType.throughput[type] > 0) {
        return 1;
      }
    }
    return 0;
  }

  public getFileSrc(fileId): string {
    return this.fileService.getLinkById(fileId);
  }

  public onChartClick(event, buildingId?: number) {
    const params = {};
    if(typeof buildingId !== 'undefined') {
      params['building'] = buildingId.toString();
    }

    for(const type in this.statusToName) {
      if(this.statusToName[type] === event.name) {
        this.router.stateService.go('archive', {
          ...params,
          'warrantyStatus': type.toUpperCase()
        });
        break;
      }
    }
  }

  public createMapBounds() {
    // create new LatLngBounds object
    const latlngbounds = new google.maps.LatLngBounds();

    // extend with buildings
    this.data.buildings.forEach((buildingBlock: any) => {
      if(buildingBlock.building.coordinate && buildingBlock.building.coordinate.coordinates) {
        latlngbounds.extend(new google.maps.LatLng({lat: buildingBlock.building.coordinate.coordinates[0], lng: buildingBlock.building.coordinate.coordinates[1]}));
      }
    });

    // if single marker, extend the viewed box
    if (latlngbounds.getNorthEast().equals(latlngbounds.getSouthWest())) {
      const extendPoint = new google.maps.LatLng(latlngbounds.getNorthEast().lat() + 0.005, latlngbounds.getNorthEast().lng() + 0.005);
      latlngbounds.extend(extendPoint);
    }

    this.map.latlngBounds = latlngbounds;
  }

  public moveMapToMarker(): void {
    if(this.selectedMarker && typeof this.selectedMarker === 'object') {
      // create new LatLngBounds object
      const latlngbounds = new google.maps.LatLngBounds();

      latlngbounds.extend(new google.maps.LatLng({lat: this.selectedMarker.lat, lng: this.selectedMarker.lng}));

      // if single marker, extend the viewed box
      if (latlngbounds.getNorthEast().equals(latlngbounds.getSouthWest())) {
        const extendPoint = new google.maps.LatLng(latlngbounds.getNorthEast().lat() + 0.005, latlngbounds.getNorthEast().lng() + 0.005);
        latlngbounds.extend(extendPoint);
      }

      this.map.latlngBounds = latlngbounds;
    } else {
      this.createMapBounds();
    }
  }

  warrantyZipUrl(id: any, assignmentId?: string) {
    let url = `${this.appConfig.apiUrl}/dashboard/warranties/${id}?access_token=${this.authService.getAccessToken()}`;
    if (assignmentId) {
      url += `&assignmentId=${assignmentId}`;
    }
    return url;
  }

  onButtonClickForExportation(channelId: string[]) {
    this.exportationModalService.showExportModal({
      channelId,
    });
  }
}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/dashboard/dashboard-client.component.ts